import type { DependsOnOption, DependsOnState } from './depends-on';
import { normalizeDependsOn, resolveDependsOn } from './depends-on';

/**
 * The slice of RJSF field props `useDependsOn` needs. Kept minimal and with
 * `registry.rootSchema` optional on purpose: the real
 * `FieldExtensionComponentProps` types declare it as always present, but a
 * host that renders the field directly (a test, a preview) can leave it out,
 * so the read stays defensive despite what the types say. See
 * `picker-toolbox/frontend/src/fields/choice-form-field.tsx:104-115`.
 */
export interface DependsOnFieldProps {
  readonly formContext?: { readonly formData?: Record<string, unknown> };
  readonly name: string;
  readonly registry: {
    readonly rootSchema?: { readonly properties?: Record<string, unknown> };
  };
}

/**
 * Every field name a `dependsOn` may legitimately reference, for the form this
 * field is rendered in.
 *
 * @remarks
 * The set is the union of two partial views, matching
 * `picker-toolbox/frontend/src/fields/choice-form-field.tsx` `validKeysFrom`.
 * Neither view is complete on its own: `formContext.formData` spans every step
 * but holds only fields the user has touched, while
 * `registry.rootSchema.properties` lists every field of the CURRENT step,
 * touched or not, but knows nothing of other steps. Reading one source alone
 * would report a legitimate reference as an unknown-field error. A key missing
 * from both is a genuine one.
 *
 * Exported because a picker that resolves its own references needs the same
 * derivation, and two copies of it would drift without anything going red.
 *
 * @param props - The field props to read the two views from.
 * @returns The referenceable field names, deduplicated.
 */
export function validKeysOf(props: DependsOnFieldProps): ReadonlySet<string> {
  const formData = props.formContext?.formData ?? {};
  const schemaProperties = props.registry.rootSchema?.properties ?? {};

  return new Set([...Object.keys(formData), ...Object.keys(schemaProperties)]);
}

/**
 * Resolves `dependsOn` for a scaffolder field against the current form.
 *
 * @remarks
 * A reference outside {@link validKeysOf} is an author error rather than a
 * value that has yet to arrive.
 *
 * @param props - The field props holding the form data and the root schema.
 * @param dependsOn - The field's `dependsOn`, as the template author wrote it.
 * @returns Whether the field is ready, still awaiting values, or misconfigured.
 */
export function useDependsOn(
  props: DependsOnFieldProps,
  dependsOn: DependsOnOption | undefined,
): DependsOnState {
  const formData = props.formContext?.formData ?? {};

  return resolveDependsOn({
    dependsOn: normalizeDependsOn(dependsOn),
    formData,
    ownName: props.name,
    validKeys: validKeysOf(props),
  });
}
